

import React, { useState } from "react";
import { Box, Button, Typography, Stack, AppBar, Toolbar, Link, Container, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Dialog, DialogContent } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../utils/userUtils";

// Geometric translucent rectangles background with blue gradient and white/light blue shapes
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      background: 'linear-gradient(135deg, #0a1628 0%, #1a2942 50%, #0d1f3c 100%)',
    }}
  >
    {/* Single subtle accent shape for minimal design */}
    <Box sx={{
      position: 'absolute',
      top: { xs: 60, md: 100 },
      right: { xs: -40, md: 80 },
      width: { xs: 200, md: 400 },
      height: { xs: 200, md: 400 },
      bgcolor: 'rgba(244, 197, 66, 0.08)',
      borderRadius: '50%',
      filter: 'blur(60px)',
      boxShadow: '0 8px 32px 0 rgba(244, 197, 66, 0.15)',
    }} />
  </Box>
);

export default function Frontpage() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const openVideo = () => {
    setVideoOpen(true);
  };
  const closeVideo = () => {
    setVideoOpen(false);
  };
  const handleLoginClick = () => {
    const user = getCurrentUser();
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }
    // Use centralized permission utility for navigation
    const { getDefaultRoute } = require("utils/permissions");
    const target = getDefaultRoute(user);
    navigate(target);
  };
  // Main theme color (match logo): #4f5ef7 (primary), #6a82fb (secondary)
  const mainGradient = 'linear-gradient(90deg, #4f5ef7 0%, #6a82fb 100%)';
  const navLinks = [
    { label: 'Utama', href: '#' },
    { label: 'Tentang Kami', href: '#' },
    { label: 'Hubungi Kami', href: '#' },
  ];
  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <AbstractBackground />
      {/* Header - Responsive with Glassmorphism */}
      <AppBar position="static" elevation={0} sx={{ background: 'rgba(10, 22, 40, 0.6)', backdropFilter: 'blur(10px)', boxShadow: 'none', pt: 2, zIndex: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Toolbar sx={{ justifyContent: 'space-between', maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 1, sm: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box component="img" src="/LogoTR.png" alt="STAIL" sx={{ height: { xs: 65, sm: 65, md: 80 }, width: { xs: 160, sm: 160, md: 200 } }} />
          </Stack>
          {/* Desktop nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
            {navLinks.map((item) => (
              <Link key={item.label} href={item.href} underline="none" sx={{ color: '#dca716', fontWeight: 500, fontSize: 16 }}>{item.label}</Link>
            ))}
            <Button
              variant="contained"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #f4c542 0%, #f39c12 100%)',
                borderRadius: 3,
                fontWeight: 600,
                px: 4,
                py: 1,
                boxShadow: '0 2px 8px rgba(244, 197, 66, 0.3)',
                letterSpacing: 1,
                color: '#0a1628',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(244, 197, 66, 0.5)',
                },
              }}
              onClick={handleLoginClick}
            >
              Login
            </Button>
          </Box>
          {/* Mobile nav: hamburger + login */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            <IconButton edge="end" color="inherit" aria-label="menu" onClick={() => setDrawerOpen(true)}>
              <MenuIcon sx={{ fontSize: 32, color: '#0d1a4d' }} />
            </IconButton>
            <Button
              variant="contained"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #f4c542 0%, #f39c12 100%)',
                borderRadius: 3,
                fontWeight: 600,
                px: 3,
                minWidth: 0,
                boxShadow: '0 2px 8px rgba(244, 197, 66, 0.3)',
                letterSpacing: 1,
                color: '#0a1628',
                ml: 1,
                fontSize: 14,
              }}
              onClick={handleLoginClick}
            >
              Login
            </Button>
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <Box sx={{ width: 220, pt: 4 }} role="presentation" onClick={() => setDrawerOpen(false)}>
                <List>
                  {navLinks.map((item) => (
                    <ListItem key={item.label} disablePadding>
                      <ListItemButton component="a" href={item.href}>
                        <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontWeight: 600, color: '#0d1a4d' } }} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Drawer>
          </Box>
        </Toolbar>
      </AppBar>
  {/* Main Content */}
  <Container maxWidth="lg" sx={{
    display: 'flex',
    alignItems: 'center',
    minHeight: '80vh',
    pt: 6,
    zIndex: 1,
    position: 'relative',
    flexDirection: { xs: 'column', md: 'row' },
    textAlign: { xs: 'center', md: 'left' },
  }}>
    {/* Stack illustration above title on mobile, right side on desktop */}
    <Box
      sx={{
        flex: 1,
        order: { xs: 0, md: 1 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'flex-end', md: 'center' },
        mb: { xs: 3, md: 0 },
        width: { xs: '100%', md: 'auto' },
        position: 'relative',
      }}
    >
      <Box
        component="img"
        src="/Logo.png"
        alt="LOGO"
        sx={{
          width: { xs: 220, sm: 300, md: 400 },
          maxWidth: '100%',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          },
        }}
      />
    </Box>
    <Box sx={{ flex: 1, order: { xs: 1, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
      {/* Glassmorphism card container */}
      <Box sx={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        borderRadius: 4,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        p: { xs: 3, md: 5 },
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
      }}>
        <Typography
          variant="h1"
          sx={{
            fontFamily: `'Poppins', 'Montserrat', 'Raleway', 'Segoe UI', Arial, sans-serif`,
            fontWeight: 900,
            color: '#ffffff',
            mb: 1,
            lineHeight: 1.2,
            letterSpacing: -1,
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem', lg: '4.5rem' },
            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          Sistem Tunggakan Hasil
        </Typography>
        {/* Add Google Fonts link for Poppins if not already in index.html */}
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&display=swap" rel="stylesheet" />
        <Typography variant="subtitle1" sx={{ color: '#f4c542', fontWeight: 600, mb: 4, letterSpacing: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
          LEMBAGA KEMAJUAN JOHOR TENGGARA
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
        <Button
          variant="contained"
          size="large"
          sx={{
            background: 'linear-gradient(135deg, #f4c542 0%, #f39c12 100%)',
            borderRadius: 3,
            fontWeight: 600,
            px: 5,
            py: 1.5,
            boxShadow: '0 4px 14px rgba(244, 197, 66, 0.4)',
            letterSpacing: 1,
            color: '#0a1628',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(244, 197, 66, 0.6)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
          onClick={() => navigate("/authentication/sign-in")}
        >
          Mulakan &rarr;
        </Button>
        <Button
          startIcon={<PlayCircleOutlineIcon />}
          variant="outlined"
          size="large"
          sx={{
            color: '#f4c542',
            borderColor: '#f4c542',
            borderRadius: 3,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontSize: 16,
            '&:hover': {
              borderColor: '#f4c542',
              backgroundColor: 'rgba(244, 197, 66, 0.1)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
          onClick={openVideo}
        >
          Lihat Video
        </Button>
        </Stack>
      </Box>
    </Box>
  </Container>
  {/* Video Dialog */}
  <Dialog
    open={videoOpen}
    onClose={closeVideo}
    maxWidth="md"
    fullWidth
    PaperProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.85)', position: 'relative', boxShadow: '0 24px 48px rgba(0,0,0,0.5)' } }}
  >
    <DialogContent sx={{ p: 0, backgroundColor: 'transparent', position: 'relative' }}>
      <Box sx={{ position: 'relative', width: '100%', backgroundColor: '#000', paddingTop: '56.25%' /* 16:9 aspect ratio */ }}>
        {/* Close button overlay */}
        <IconButton
          aria-label="Tutup video"
            onClick={closeVideo}
            size="small"
            sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {/* Optional subtle title overlay (visually minimal) */}
        <Typography
          variant="caption"
          sx={{ position: 'absolute', left: 12, top: 10, zIndex: 1, color: 'rgba(255,255,255,0.85)', letterSpacing: 1, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
        >
          Video Pengenalan STAIL
        </Typography>
        <iframe
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          src="https://www.youtube.com/embed/3URNZu47XF4?si=4QnS0ML9tTlaJHcP"
          title="Video Pengenalan STAIL"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Box>
    </DialogContent>
  </Dialog>
    </Box>
  );
}
